import { useState, useRef } from 'react';

/**
 * SignaturePicker — Draw / Upload / URL signature input.
 * Props:
 *   value    {string}   — data URL or image URL (empty = no signature)
 *   onChange {Function} — called with new data URL / URL, or '' to clear
 */
export default function SignaturePicker({ value, onChange }) {
  const [tab, setTab] = useState('draw');
  const [urlInput, setUrlInput] = useState('');
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  // ── Preview ────────────────────────────────────────────────────────────────
  if (value) {
    return (
      <div style={{
        border: '1.5px solid rgba(26,26,46,0.2)',
        borderRadius: 6,
        padding: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: '#fff',
      }}>
        <img
          src={value}
          alt="Signature"
          style={{ maxHeight: 48, maxWidth: 'calc(100% - 60px)', objectFit: 'contain', display: 'block' }}
        />
        <button
          onClick={() => onChange('')}
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            color: '#ef4444',
            background: 'none',
            border: '1px solid rgba(239,68,68,0.3)',
            cursor: 'pointer',
            padding: '2px 8px',
            borderRadius: 4,
            flexShrink: 0,
          }}
        >
          Clear
        </button>
      </div>
    );
  }

  // ── Canvas helpers ─────────────────────────────────────────────────────────
  function getPos(e) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * (canvas.width / rect.width),
      y: (src.clientY - rect.top) * (canvas.height / rect.height),
    };
  }

  function startDraw(e) {
    drawing.current = true;
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    e.preventDefault();
  }

  function continueDraw(e) {
    if (!drawing.current) return;
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    e.preventDefault();
  }

  function endDraw() { drawing.current = false; }

  function clearCanvas() {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
  }

  function saveDrawing() {
    onChange(canvasRef.current.toDataURL('image/png'));
  }

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => onChange(ev.target.result);
    reader.readAsDataURL(file);
  }

  // ── Styles ─────────────────────────────────────────────────────────────────
  const tabStyle = (t) => ({
    padding: '5px 12px',
    fontSize: 11,
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    background: tab === t ? 'rgba(26,26,46,0.07)' : 'transparent',
    borderBottom: tab === t ? '2px solid #1a1a2e' : '2px solid transparent',
    color: tab === t ? '#1a1a2e' : '#9ca3af',
    transition: 'all 0.15s',
  });

  const actionBtn = (primary) => ({
    fontSize: 11,
    padding: '3px 10px',
    borderRadius: 4,
    border: primary ? 'none' : '1px solid rgba(26,26,46,0.2)',
    cursor: 'pointer',
    background: primary ? '#1a1a2e' : '#fff',
    color: primary ? '#fff' : '#6b7280',
  });

  return (
    <div style={{ border: '1.5px solid rgba(26,26,46,0.2)', borderRadius: 6, overflow: 'hidden', background: '#fff' }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(26,26,46,0.1)' }}>
        {[['draw', '✏ Draw'], ['upload', '↑ Upload'], ['url', '🔗 URL']].map(([t, label]) => (
          <button key={t} style={tabStyle(t)} onClick={() => setTab(t)}>{label}</button>
        ))}
      </div>

      {/* Draw */}
      {tab === 'draw' && (
        <div>
          <canvas
            ref={canvasRef}
            width={500}
            height={100}
            style={{ width: '100%', height: 80, display: 'block', cursor: 'crosshair', touchAction: 'none' }}
            onMouseDown={startDraw}
            onMouseMove={continueDraw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={continueDraw}
            onTouchEnd={endDraw}
          />
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 6,
            padding: '5px 8px', borderTop: '1px solid rgba(26,26,46,0.08)', background: 'rgba(0,0,0,0.02)',
          }}>
            <button style={actionBtn(false)} onClick={clearCanvas}>Clear</button>
            <button style={actionBtn(true)} onClick={saveDrawing}>Save</button>
          </div>
        </div>
      )}

      {/* Upload */}
      {tab === 'upload' && (
        <div style={{ padding: '12px 10px' }}>
          <input type="file" accept="image/*" onChange={handleFile} style={{ fontSize: 12 }} />
        </div>
      )}

      {/* URL */}
      {tab === 'url' && (
        <div style={{ display: 'flex', gap: 6, padding: '8px' }}>
          <input
            type="text"
            value={urlInput}
            onChange={e => setUrlInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && urlInput.trim() && onChange(urlInput.trim())}
            placeholder="https://example.com/signature.png"
            style={{
              flex: 1, fontSize: 12, padding: '4px 8px',
              border: '1px solid rgba(26,26,46,0.2)', borderRadius: 4, outline: 'none',
            }}
          />
          <button
            onClick={() => urlInput.trim() && onChange(urlInput.trim())}
            style={actionBtn(true)}
          >
            Use
          </button>
        </div>
      )}
    </div>
  );
}
